import {Component, effect, EventEmitter, Input, OnDestroy, Output, Signal, signal, inject} from '@angular/core';
import {
  NbButtonModule,
  NbDialogService,
  NbIconModule,
  NbSpinnerModule,
  NbToastrService,
  NbTooltipModule
} from "@nebular/theme";
import {Utils} from "../../../../shared/utils";
import {MatchesService} from "../../../../services/matches.service";
import {DatePipe} from "@angular/common";
import {ConfirmDialogComponent} from "../../../../shared/components/confirm-dialog/confirm-dialog.component";
import {ProposeNewDateFormComponent} from "./propose-new-time-form/propose-new-date-form.component";
import {notificationsParams} from "../../../../shared/settings";
import {TeamsService} from "../../../../services/teams.service";
import {forkJoin, interval, Subscription} from "rxjs";
import {TranslateModule, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-planning',
  standalone: true,
  imports: [
    TranslateModule,
    NbButtonModule,
    DatePipe,
    NbSpinnerModule,
    NbIconModule,
    NbTooltipModule
  ],
  templateUrl: './planning.component.html',
  styleUrl: './planning.component.scss'
})
export class PlanningComponent implements OnDestroy {
  private readonly translate = inject(TranslateService);
  private sub!: Subscription;
  private refreshTick = signal(0);

  loadingPendingMatch = false;
  loadingProposedMatch = false;
  loadingConfirmedMatch = false;

  selectedDivisionId: string | undefined;
  selectedGroupId: string | undefined;

  pendingMatches = signal<any>({});
  proposedMatches = signal<any>({});
  confirmedMatches = signal<any>({});
  countNbNotification = signal<number>(0);

  @Input() currentUserTeamIds!: Signal<any>;
  @Input() triggerTabsRefresh!: Signal<string>;
  @Output() onMatchUpdate = new EventEmitter<any>();
  @Output() nbNotificationsChanged = new EventEmitter<number>();

  constructor(public utils: Utils, protected teamsService: TeamsService, private matchesService: MatchesService, private dialogService: NbDialogService, private toastrService: NbToastrService) {
    effect(
      () => {
        this.refreshTick();

        const refreshTriggeredBy = this.triggerTabsRefresh();
        if (refreshTriggeredBy == 'by_planning') {
          return;
        }

        this.search();
      },
      { allowSignalWrites: true }
    );

    // ⏱ Rafraîchissement toutes les minutes
    this.sub = interval(120_000).subscribe(() => {
      this.refreshTick.update(v => v + 1);
    });
  }

  ngOnInit() {
    this.countNbNotification.set(0);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  search() {
    this.loadingPendingMatch = true;
    this.loadingProposedMatch = true;
    this.loadingConfirmedMatch = true;

    const observable = forkJoin({
      pendingMatches: this.matchesService.pendingMatches(),
      proposedMatches: this.matchesService.proposedMatches(),
      confirmedMatches: this.matchesService.confirmedMatches()
    });

    observable.subscribe((result) => {
      this.countNbNotification.set(0);

      this.pendingMatches.set(result.pendingMatches);
      this.countNbNotification.update(v => v + result.pendingMatches.length);
      this.nbNotificationsChanged.emit(this.countNbNotification());
      this.loadingPendingMatch = false;

      this.proposedMatches.set(result.proposedMatches);
      this.countNbNotification.update(v => v + result.proposedMatches.length);
      this.nbNotificationsChanged.emit(this.countNbNotification());
      this.loadingProposedMatch = false;

      this.confirmedMatches.set(result.confirmedMatches);
      this.countNbNotification.update(v => v + result.confirmedMatches.length);
      this.nbNotificationsChanged.emit(this.countNbNotification());
      this.loadingConfirmedMatch = false;
    });
  }

  openConfirmCancel(match: any) {
    this.dialogService.open(ConfirmDialogComponent).onClose.subscribe((result) => {
      if (result) {
        this.cancelMatch(match);
      }
    });
  }

  openConfirmAccept(match: any) {
    this.dialogService.open(ConfirmDialogComponent).onClose.subscribe((result) => {
      if (result) {
        this.acceptMatch(match);
      }
    });
  }

  openConfirmDecline(match: any) {
    this.dialogService.open(ConfirmDialogComponent).onClose.subscribe((result) => {
      if (result) {
        this.declineMatch(match);
      }
    });
  }

  editPendingMatch(match: any) {
    if (match.status !== "pending") {
      this.toastrService.danger(this.translate.instant('games.match.cannot_edit'), this.translate.instant('common.error'), notificationsParams);
      return;
    }
    this.dialogService.open(ProposeNewDateFormComponent, {
      context: {
        match: match,
      },
    }).onClose.subscribe(() => this.search());
  }

  openProposeNewTimeForm(match: any) {
    this.dialogService.open(ProposeNewDateFormComponent, {
      context: {
        match: match,
      },
    }).onClose.subscribe(() => this.search());
  }

  private cancelMatch(match: any) {
    this.matchesService.cancelMatch(match.match_id).subscribe((response: any) => {
      this.toastrService.success(this.translate.instant('games.match.canceled'), this.translate.instant('common.information'), notificationsParams);
      this.search();
      this.onMatchUpdate.emit('by_planning');
    });
  }

  private acceptMatch(match: any) {
    this.matchesService.acceptMatch(match.match_id).subscribe((response: any) => {
      this.toastrService.success(this.translate.instant('games.match.accepted'), this.translate.instant('common.information'), notificationsParams);
      this.search();
      this.onMatchUpdate.emit('by_planning');
    });
  }

  private declineMatch(match: any) {
    this.matchesService.declineMatch(match.match_id).subscribe((response: any) => {
      this.toastrService.success(this.translate.instant('games.match.declined'), this.translate.instant('common.information'), notificationsParams);
      this.search();
    });
    this.onMatchUpdate.emit('by_planning');
  }
}
